import { useState, useEffect, useCallback } from "react";
import "./../../assets/css/views/subRetailManagement/posManagement.css";
import {
  Button,
  Checkbox,
  IconHelp,
  Modal,
  Select,
  SelectOption,
  TextBox,
} from "@veneer/core";
import MapComponent from "../../components/Maps/MapComponent";
import { useTranslation } from "react-i18next";
import PointofSalesLogic from "./PointofSalesLogic";
import { FormFields, IPosIn } from "../../models/input/agendaApi/IPosIn";
import LoaderModal from "../../components/Loader/loaderModal";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { decryptText } from "../../utils/encryptionUtils";
import useSearchSelect from "../../hooks/searchSelect/useSearchSelect";

export default function CreatePOS() {
  const state: any = useSelector((state: RootState) => state.user);
  const [coordinates, setCoordinates] = useState<google.maps.LatLngLiteral>({
    lat: 0,
    lng: 0,
  });
  const [markers, setMarkers] = useState<
    { position: google.maps.LatLngLiteral; title: string }[]
  >([]);
  const [lat, setLat] = useState<number>(0);
  const [lng, setLng] = useState<number>(0);
  const [messageModal, setMessageModal] = useState({
    title: "",
    message: "",
  });
  const [openModal, setOpenModal] = useState(false);
  const [latitude, setLatitude] = useState<string>("");
  const [longitude, setLongitude] = useState<string>("");
  const [isBrasil, setIsBrasil] = useState(false);
  const [checkOnline, setCheckOnline] = useState(false);
  const { handleSearch } = useSearchSelect();

  const [t] = useTranslation("global");
  const {
    getSubRetail,
    getClassList,
    getCountryList,
    getStatesList,
    getCityList,
    getStratificationList,
    insertPOS,
    dataSubRetailList,
    dataClassList,
    dataCountryList,
    dataStateList,
    dataCityList,
    handleChangeSearch,
    dataStratificationList,
    setDataStratificationList,
    loading,
    formPointOfSale,
    setFormPointOfSale,
    currentDataSelect,
    setDataSubRetailList,
    setDataCityList,
    setDataClassList,
    setDataCountryList,
    setDataStateList,
    getBrandsData,
    valueBrands,
    optionsBrandsCopy,
    setOptionsBrands,
    optionsBrands,
    onChange,
    setValueBrands,
  } = PointofSalesLogic();

  useEffect(() => {
    getSubRetail();
    getCountryList();
    getClassList();
    getStratificationList();
    getBrandsData();
    setIsBrasil(
      [13, 28, 47, 63, 65].includes(Number(decryptText(state.user.countryId)))
    );
  }, []);

  useEffect(() => {
    // Fetch current position only once when the component mounts
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setCoordinates(newLocation);
          setMarkers([{ position: newLocation, title: "You are here" }]);
          setLat(position.coords.latitude);
          setLng(position.coords.longitude);
          setLatitude(position.coords.latitude.toString());
          setLongitude(position.coords.longitude.toString());
        },
        (error) => {
          console.error("Error obtaining location", error);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 5000,
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  }, []);

  const handleMapClick = (event: google.maps.MapMouseEvent) => {
    const { latLng } = event;
    if (latLng) {
      const newCoordinates = { lat: latLng.lat(), lng: latLng.lng() };
      setCoordinates(newCoordinates);
      setMarkers([{ position: newCoordinates, title: "Clicked Location" }]);
      setLat(newCoordinates.lat);
      setLng(newCoordinates.lng);
      setLatitude(newCoordinates.lat.toString());
      setLongitude(newCoordinates.lng.toString());
    }
  };
  const handleLatChange = (e: string) => {
    setLatitude(e);
  };

  const handleLngChange = (e: string) => {
    setLongitude(e);
  };

  const isFormComplete = (): boolean => {
    const { cnpj, ...fieldsToCheck } = formPointOfSale;

    const optionalFieldsWhenOnline = new Set([
      "countryId",
      "stateId",
      "cityId",
      "longitude",
      "latitude",
      "brands",
    ]);

    let allFieldsComplete = true;

    for (const [key, value] of Object.entries(fieldsToCheck)) {
      if (optionalFieldsWhenOnline.has(key) && checkOnline) {
        continue;
      }

      // Validación especial para brands (number[])
      if (key === "brands") {
        if (
          !Array.isArray(valueBrands) ||
          valueBrands.length <= 1 ||
          !valueBrands.includes(1)
        ) {
          allFieldsComplete = false;
          break;
        }
        continue; // ya validado
      }

      if (value.length === 0) {
        allFieldsComplete = false;
        break;
      }
    }
    const isCNPJComplete = checkOnline ? true : allFieldsComplete;
    return allFieldsComplete && isCNPJComplete;
  };

  const handleSaveLocation = () => {
    setLat(Number(latitude));
    setLng(Number(longitude));
    const newCoordinates = { lat: Number(latitude), lng: Number(longitude) };
    setCoordinates(newCoordinates);
    setMarkers([{ position: newCoordinates, title: "Saved Location" }]);
  };

  const onChangeForm = useCallback(
    (option: { value: any }, type: keyof FormFields) => {
      setFormPointOfSale((prev) => {
        const isEmptyValue = option.value === "";
        const updatedForm = {
          ...prev,
          [type]: isEmptyValue ? [] : [option.value],
        };

        if (isEmptyValue) {
          if (type === "countryId") {
            updatedForm.stateId = [];
            updatedForm.cityId = [];
          } else if (type === "stateId") {
            updatedForm.cityId = [];
          }
        } else {
          switch (type) {
            case "countryId":
              updatedForm.stateId = [];
              updatedForm.cityId = [];
              getStatesList(option.value);
              break;
            case "stateId":
              updatedForm.cityId = [];
              getCityList(option.value);
              break;
            case "brands":
              onChange({ value: Number(option.value.value ?? 0) });
              break;
            default:
              break;
          }
        }

        return updatedForm;
      });
    },
    [getStatesList, getCityList]
  );

  const clearfieldFromForm = (field: keyof FormFields) => {
    setFormPointOfSale((prev: FormFields) => {
      const newState: Partial<FormFields> = { [field]: [] };
      if (field === "countryId") {
        newState.stateId = [];
        newState.cityId = [];
      } else if (field === "stateId") {
        newState.cityId = [];
      } else if (field === "brands") {
        setValueBrands([]);
      }
      return { ...prev, ...newState };
    });
  };

  const getModalCode = () => {
    setOpenModal(true);
    setMessageModal({
      title: t("posManagement.posCode"),
      message: t("posManagement.msgPosCode"),
    });
  };

  const getModalPointSale = () => {
    setOpenModal(true);
    setMessageModal({
      title: t("posManagement.pointOfSale"),
      message: t("posManagement.msgPointOfSale"),
    });
  };

  const getModalBrands = () => {
    setOpenModal(true);
    setMessageModal({
      title: t("header.brands"),
      message: t("modal.helperBrands"),
    });
  };

  const isEnabled = (type: string[]) => {
    if (checkOnline || type.length === 0) {
      return true;
    } else {
      return false;
    }
  };

  const createPOS = () => {
    const params: IPosIn = {
      userID: 0,
      subRetailID: formPointOfSale.subretailId[0]
        ? Number(formPointOfSale.subretailId[0])
        : 0,
      pos: formPointOfSale.pointOfSale[0] ?? null,
      poS_ClassID: formPointOfSale.claseId[0]
        ? Number(formPointOfSale.claseId[0])
        : 0,
      onLine: checkOnline ? 1 : 0,
      countryID: checkOnline ? 0 : Number(formPointOfSale.countryId[0]) ?? 0,
      stateID: checkOnline ? 0 : Number(formPointOfSale.stateId[0]) ?? 0,
      cityID: checkOnline ? 0 : Number(formPointOfSale.cityId[0]) ?? 0,
      stratificationID: formPointOfSale.economyId[0]
        ? Number(formPointOfSale.economyId[0])
        : 0,
      longitude: checkOnline ? null : longitude ?? null,
      latitude: checkOnline ? null : latitude ?? null,
      poS_Code: formPointOfSale.posCode[0] ?? null,
      cnpj: formPointOfSale.cnpj[0] ?? null,
      address: "",
      brandIDs: valueBrands,
    };
    insertPOS(params);
  };

  return (
    <div className="generalPOSDiv">
      <div className="posManagementTopElements">
        <div className="divForm">
          <h4 className="title">{t("posManagement.createPOS")}</h4>
          <div className="form">
            <Select
              className="selectGeneralPOS"
              options={dataSubRetailList}
              id="select-usage"
              label={t("posManagement.subRetail")}
              placement="bottom"
              helperTextVisibility="auto"
              onChange={(value) => onChangeForm(value, "subretailId")}
              onClear={() => clearfieldFromForm("subretailId")}
              value={[Number(formPointOfSale.subretailId[0])]}
              disabled={false}
              showSearch
              onSearch={(value) =>
                handleChangeSearch(
                  value,
                  currentDataSelect.dataSubRetailList,
                  setDataSubRetailList
                )
              }
            />
            <div className="containerWithIconPOS">
              <TextBox
                label={t("posManagement.posCode")}
                id="posCode"
                className="posManagementTextbox"
                type="text"
                value={formPointOfSale.posCode[0] ?? ""}
                maxLength={100}
                onChange={(value) => onChangeForm({ value: value }, "posCode")}
              />
              <Button
                className="helpButtonPOS"
                leadingIcon={<IconHelp />}
                onClick={getModalCode}
              ></Button>
            </div>
            <div className="containerWithIconPOS">
              <TextBox
                label={t("posManagement.pointOfSale")}
                id="pointOfSale"
                className="textBoxContainer"
                type="text"
                value={formPointOfSale.pointOfSale[0] ?? ""}
                maxLength={100}
                onChange={(value) =>
                  onChangeForm({ value: value }, "pointOfSale")
                }
              />
              <Button
                className="helpButtonPOS"
                leadingIcon={<IconHelp />}
                onClick={getModalPointSale}
              ></Button>
            </div>
            <div className="containerClassPOS">
              <Select
                className="selectGeneralPOS"
                options={dataClassList}
                id="select-usage"
                label={t("posManagement.class")}
                placement="bottom"
                helperTextVisibility="auto"
                onChange={(value) => onChangeForm(value, "claseId")}
                onClear={() => clearfieldFromForm("claseId")}
                value={[Number(formPointOfSale.claseId[0])]}
                disabled={false}
                showSearch
                onSearch={(value) =>
                  handleChangeSearch(
                    value,
                    currentDataSelect.dataClassList,
                    setDataClassList
                  )
                }
              />
              <div className="checkContainerPOS">
                <Checkbox
                  label={t("header.online")}
                  className="registerSalePriceCheckbox"
                  id="checkbox"
                  value={checkOnline}
                  checked={checkOnline}
                  onChange={() => setCheckOnline((prev) => !prev)}
                />
              </div>
            </div>
            <Select
              className="selectGeneralPOS"
              options={dataCountryList}
              id="select-usage"
              label={t("header.country")}
              placement="bottom"
              helperTextVisibility="auto"
              onChange={(value) => onChangeForm(value, "countryId")}
              onClear={() => clearfieldFromForm("countryId")}
              value={[Number(formPointOfSale.countryId[0])]}
              disabled={checkOnline}
              showSearch
              onSearch={(value) =>
                handleChangeSearch(
                  value,
                  currentDataSelect.dataCountryList,
                  setDataCountryList
                )
              }
            />
            <Select
              className="selectGeneralPOS"
              options={dataStateList}
              id="select-usage"
              label={t("header.state")}
              placement="bottom"
              helperTextVisibility="auto"
              onChange={(value) => onChangeForm(value, "stateId")}
              onClear={() => clearfieldFromForm("stateId")}
              value={[Number(formPointOfSale.stateId)]}
              disabled={isEnabled(formPointOfSale.countryId)}
              showSearch
              onSearch={(value) =>
                handleChangeSearch(
                  value,
                  currentDataSelect.dataStateList,
                  setDataStateList
                )
              }
            />
            <Select
              className="selectGeneralPOS"
              options={dataCityList}
              id="select-usage"
              label={t("posManagement.city")}
              placement="bottom"
              helperTextVisibility="auto"
              onChange={(value) => onChangeForm(value, "cityId")}
              onClear={() => clearfieldFromForm("cityId")}
              value={[Number(formPointOfSale.cityId[0])]}
              disabled={isEnabled(formPointOfSale.stateId)}
              showSearch
              onSearch={(value) =>
                handleChangeSearch(
                  value,
                  currentDataSelect.dataCityList,
                  setDataCityList
                )
              }
            />
            <Select
              className="selectGeneralPOS"
              options={dataStratificationList}
              id="select-usage"
              label={t("posManagement.stratification")}
              placement="bottom"
              helperTextVisibility="auto"
              onChange={(value) => onChangeForm(value, "economyId")}
              onClear={() => clearfieldFromForm("economyId")}
              value={[Number(formPointOfSale.economyId)]}
              disabled={false}
              showSearch
              onSearch={(value) =>
                handleChangeSearch(
                  value,
                  currentDataSelect.dataStratification,
                  setDataStratificationList
                )
              }
            />

            {/* Brands */}
            <div className="containerWithIconPOS">
              <Select
                className="textBoxContainer"
                multiple
                required
                showSearch
                onSearch={(query) =>
                  handleSearch(query, optionsBrandsCopy, setOptionsBrands)
                }
                options={optionsBrands as SelectOption<number>[]}
                id="select-multiple-usage"
                helperTextVisibility="auto"
                label="Seleccionar Marcas"
                placement="bottom"
                onChange={(value) => onChangeForm({ value: value }, "brands")}
                onClear={() => clearfieldFromForm("brands")}
                value={valueBrands}
              />
              <Button
                className="helpButtonPOS"
                leadingIcon={<IconHelp />}
                onClick={getModalBrands}
              ></Button>
            </div>
            {/* Brands */}

            {isBrasil && (
              <TextBox
                label={t("posManagement.cnpj")}
                id="CNPJ"
                className="posManagementTextbox"
                type="text"
                value={formPointOfSale.cnpj[0] ?? ""}
                maxLength={100}
                onChange={(value) => onChangeForm({ value: value }, "cnpj")}
              />
            )}
            <div className="divButtonAddPOS">
              <Button
                className="buttonAddPOS"
                onClick={createPOS}
                disabled={!isFormComplete()}
              >
                {t("header.save")}
              </Button>
            </div>
          </div>
        </div>
        <div className="divMapCoordenatesPOS">
          <div className="posManagementDivMap">
            <MapComponent
              center={coordinates}
              zoom={13}
              markers={markers}
              onMapClick={handleMapClick}
            />
          </div>
          <div className="coordinates-inputs-pos">
            <TextBox
              label={t("posManagement.latitude")}
              id="latitude"
              className="textbox"
              type="number"
              value={latitude.toString()}
              onChange={(e) => handleLatChange(e)}
              disabled={checkOnline}
            />
            <TextBox
              label={t("posManagement.longitude")}
              id="longitude"
              className="textbox"
              type="number"
              maxLength={100}
              value={longitude.toString()}
              onChange={(e) => handleLngChange(String(e))}
              disabled={checkOnline}
            />
            <Button
              onClick={handleSaveLocation}
              disabled={lng != 0 && lat != 0 && !checkOnline ? false : true}
            >
              {t("header.search")}
            </Button>
          </div>
        </div>
      </div>
      <Modal
        align="start"
        id="modal-id"
        show={openModal}
        onClose={() => {}}
        footer={
          <div style={{ textAlign: "center" }}>
            <Button
              style={{ marginRight: 4 }}
              onClick={() => setOpenModal(false)}
            >
              {t("header.confirm")}
            </Button>
          </div>
        }
        title={messageModal.title}
      >
        <p>{messageModal.message}</p>
      </Modal>
      {loading && (
        <div>
          <LoaderModal>
            <div> {t("modal.savingInformation")}</div>
          </LoaderModal>
        </div>
      )}
    </div>
  );
}
